/**
 * @module app.cm
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { modelIdMatcher } from 'app/shared/lib/matcher';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import { validate } from 'app/shared/elements/ess-rule';

@autoinject
export class ReferCase extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    // bindables
    @bindable
    public memberId: string = '';

    @bindable
    public taskId: string = '';

    @bindable
    public originCode: string = '';

    @bindable
    public showCriteria: boolean = false; // TODO: implement this

    @bindable
    public showAssign: boolean = true;

    // selected values
    protected assignedTo: Core.User | null = null;
    protected source: CM.ReferralSource | null = null;
    protected destination: CM.ReferralDestination | null = null;
    protected selectedTypes: CM.ReferralType[] = [];
    protected reason: CM.ReferralReason | null = null;
    protected comments: string = '';

    // lookups
    protected users: Core.User[] = [];
    protected sources: CM.ReferralSource[] = [];
    protected destinations: CM.ReferralDestination[] = [];
    protected types: CM.ReferralType[] = [];
    protected reasons: CM.ReferralReason[] = [];
    protected showReferralTypes: boolean = false;

    // object matchers
    protected modelMatcher = modelIdMatcher;

    protected validate = validate;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(private readonly element: Element, private readonly service: CaseService, ...rest: any[]) {
        super(...rest);
    }

    //protected created(owningView: View, myView: View): void { }
    //protected bind(bindingContext: Object, overrideContext: Object): void { }

    protected attached(): void {
        // load lookups, do not wait for results
        this.loadLookups();
    }

    //protected deactivate(): void { }
    //protected detached(): void { }
    //protected unbind(): void { }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////

    public hasChanges(): boolean {
        return true;
    }

    public async submit(): Promise<boolean> {
        return !!(await this.createCase());
    }

    ////****************** Service Calls ********************************************////

    private async createCase(): Promise<string> {

        if(!validate(this.element)) {
            return '';
        }

        // TODO: remove 'any' once essette-api.ts is updated
        const metadata: any = !this.taskId ? undefined : {
            created: {
                byTask: { id: this.taskId }
            }
        };

        const saveCase: CM.Case = {
            member: { id: this.memberId || null },
            assignedTo: getReference(this.assignedTo),
            metadata,
            referral: {
                verification: this.showCriteria ? { /* criteria: getReferences(this.selectedCriteria) */ } : undefined,
                source: getReference(this.source),
                destination: getReference(this.destination),
                origin: { code: this.originCode || 'ManageCases' },
                reason: getReference(this.reason),
                types: getReferences(this.selectedTypes),
                comments: this.comments || undefined
            }
        };

        try {
            this.state.beginSave();
            const response = await this.service.insertCase(saveCase);
            return response.id || '';
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }

    }

    private async loadLookups(): Promise<void> {

        const [users, sources, destinations, types, reasons] = await Promise.all([
            // TODO: reverse member filter search for users that can see member
            this.showAssign ?
                this.service.loadCaseOwners({ all: true }) :
                createQueryResults<CM.CaseOwner>('CM.CaseOwner', []),
            this.service.loadReferralSources({ all: true }),
            this.service.loadReferralDestinations({ all: true }),
            this.service.loadReferralTypes({ all: true }, ["criteria"]),
            this.service.loadReferralReasons({ all: true })
        ]);

        this.users = users.results;
        this.sources = sources.results;
        this.destinations = destinations.results;
        this.types = types.results.filter(t => t.criteria && t.criteria.length > 0);
        this.showReferralTypes = this.types.length > 0 && this.types.some(t => t.criteria && t.criteria.length > 0);
        this.reasons = reasons.results;

    }

}
